import { last } from "@prismicio/editor-support/Array"
import {
  BlankSlate,
  BlankSlateDescription,
  BlankSlateIcon,
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogContent,
  DialogHeader,
  FileExplorer,
  FileExplorerFile,
  FileExplorerFolder,
  FileExplorerLabel,
  FileExplorerList,
  FileExplorerViewer,
  Icon,
  installColorScheme,
  ListItem,
  Text,
  uninstallColorScheme,
  UnorderedList,
  useFileExplorer,
} from "@prismicio/editor-ui"
import { type ReactNode, type SVGProps, useLayoutEffect } from "react"

import { trackDevFileViewed } from "../../analytics/tracking"
import styles from "./DevDialog.module.css"
import customtypes__page__index_json from "./project-nextjs/customtypes/page/index.json?shiki"
import package_json from "./project-nextjs/package.json?shiki"
import prismicio_types_d_ts from "./project-nextjs/prismicio-types.d.ts?shiki"
import slicemachine_config_json from "./project-nextjs/slicemachine.config.json?shiki"
import app__uid__page_tsx from "./project-nextjs/src/app/[uid]/page.tsx?shiki"
import app__api__exit_preview__route_ts from "./project-nextjs/src/app/api/exit-preview/route.ts?shiki"
import app__api__preview__route_ts from "./project-nextjs/src/app/api/preview/route.ts?shiki"
import app__api__revalidate__route_ts from "./project-nextjs/src/app/api/revalidate/route.ts?shiki"
import app__layout_tsx from "./project-nextjs/src/app/layout.tsx?shiki"
import app__page_tsx from "./project-nextjs/src/app/page.tsx?shiki"
import app__slice_simulator__page_tsx from "./project-nextjs/src/app/slice-simulator/page.tsx?shiki"
import prismicio_ts from "./project-nextjs/src/prismicio.ts?shiki"
import slices__Hero__index_tsx from "./project-nextjs/src/slices/Hero/index.tsx?shiki"
import slices__Hero__mocks_json from "./project-nextjs/src/slices/Hero/mocks.json?shiki"
import slices__Hero__model_json from "./project-nextjs/src/slices/Hero/model.json?shiki"
import slices__index_ts from "./project-nextjs/src/slices/index.ts?shiki"

const files = {
  "customtypes/page/index.json": {
    contents: { __html: customtypes__page__index_json },
    description: (
      <>
        <Text>
          Content models are created in Slice Machine and saved in the{" "}
          <CodeSpan>customtypes</CodeSpan> directory. Saving the model as a JSON
          file in a website’s repository allows the model to be version
          controlled using Git.
        </Text>
        <Text>
          Websites can have as many content models as necessary. “Page” is a
          common type used to represent pages in the website.
        </Text>
      </>
    ),
  },
  "slices/index.ts": {
    contents: { __html: slices__index_ts },
    description: (
      <>
        <Text>
          Each slice’s component is registered in this file. It is generated
          automatically by Slice Machine anytime a slice is created, updated, or
          deleted.
        </Text>

        <Text>
          Components are lazy-loaded by default, reducing a website’s initial
          loading time.
        </Text>
        <Text>
          See how the <CodeSpan>components</CodeSpan> export is used to render
          pages with the <CodeSpan>&lt;SliceZone&gt;</CodeSpan> component in{" "}
          <CodeSpan>app/[uid]/page.tsx</CodeSpan>.
        </Text>
      </>
    ),
  },
  "slices/Hero/index.tsx": {
    contents: { __html: slices__Hero__index_tsx },
    description: (
      <>
        <Text>
          Slice components are standard React components. They are used to
          render a page’s slice using the <CodeSpan>&lt;SliceZone&gt;</CodeSpan>{" "}
          component. Content is provided via the <CodeSpan>slice</CodeSpan>{" "}
          prop.
        </Text>
        <Text>
          This example shows how a slice can render images, links, and rich text
          using components from <CodeSpan>@prismicio/react</CodeSpan> and{" "}
          <CodeSpan>@prismicio/next</CodeSpan>. Any styling solution can be used
          with Prismic websites, including Tailwind CSS.
        </Text>
        <Text>
          See how this component is registered in{" "}
          <CodeSpan>slices/index.ts</CodeSpan> and rendered via{" "}
          <CodeSpan>&lt;SliceZone&gt;</CodeSpan> in{" "}
          <CodeSpan>app/[uid]/page.tsx</CodeSpan>.
        </Text>
      </>
    ),
  },
  "slices/Hero/model.json": {
    contents: { __html: slices__Hero__model_json },
    description: (
      <Text>
        Slice content models are created in Slice Machine and saved in{" "}
        <CodeSpan>model.json</CodeSpan> files. Saving the model as a JSON file
        in a website’s repository allows developers to use Git to version
        control their models.
      </Text>
    ),
  },
  "slices/Hero/mocks.json": {
    contents: { __html: slices__Hero__mocks_json },
    description: (
      <>
        <Text>
          With Slice Machine, developers can create slice components in an
          isolated simulated environment. Mock content lets developers test
          slice components with mocked content. Mocked content is saved in{" "}
          <CodeSpan>mocks.json</CodeSpan> to be recalled when re-opening the
          simulator.
        </Text>
        <Text>
          See how the slice simulator page is registered in{" "}
          <CodeSpan>app/slice-simulator/page.tsx</CodeSpan>.
        </Text>
      </>
    ),
  },
  "app/layout.tsx": {
    contents: { __html: app__layout_tsx },
    description: (
      <>
        <Text>
          Prismic websites use a standard Next.js layout with one addition:{" "}
          <CodeSpan>&lt;PrismicPreview&gt;</CodeSpan>.
        </Text>
        <Text>
          The preview component gives content writers the ability to preview
          their changes live before publishing. It works in combination with the{" "}
          <CodeSpan>app/api/preview/route.ts</CodeSpan> and{" "}
          <CodeSpan>app/api/exit-preview/route.ts</CodeSpan> Route Handers.
        </Text>
      </>
    ),
  },
  "app/page.tsx": {
    contents: { __html: app__page_tsx },
    description: (
      <>
        <Text>
          Pages are written using standard Next.js App Router conventions.
        </Text>
        <Text>
          Content is queried in the page using{" "}
          <CodeSpan>@prismicio/client</CodeSpan>. The{" "}
          <CodeSpan>getSingle</CodeSpan> method fetches a document where only
          one should exist, such as the website's homepage.
        </Text>
        <Text>
          The page's slices are rendered using{" "}
          <CodeSpan>&lt;SliceZone&gt;</CodeSpan>. It renders each slice as a
          React component, which are written in the <CodeSpan>slices</CodeSpan>{" "}
          directory.
        </Text>
      </>
    ),
  },
  "app/slice-simulator/page.tsx": {
    contents: { __html: app__slice_simulator__page_tsx },
    description: (
      <>
        <Text>
          Developers can preview slices in Slice Machine while coding in their
          own editor. Live previews are powered by the website’s{" "}
          <CodeSpan>/slice-simulator</CodeSpan> route.
        </Text>
        <Text>
          The route uses the <CodeSpan>&lt;SliceSimulator&gt;</CodeSpan>{" "}
          component from{" "}
          <CodeSpan>@slicemachine/adapter-next/simulator</CodeSpan>. It connects
          to Slice Machine’s simulator and renders mock slice content.
        </Text>
        <Text>
          With the simulator, you can develop slices quickly in isolation.
        </Text>
      </>
    ),
  },
  "app/[uid]/page.tsx": {
    contents: { __html: app__uid__page_tsx },
    description: (
      <>
        <Text>
          Pages are written using standard Next.js App Router conventions.
        </Text>
        <Text>
          This page uses a dynamic route segment: <CodeSpan>[uid]</CodeSpan>.
          Using this route, page URLs are determined by the Prismic document’s
          UID field. Content is queried from Prismic using the{" "}
          <CodeSpan>getByUID</CodeSpan> client method.
        </Text>
        <Text>
          The page's slices are rendered using{" "}
          <CodeSpan>&lt;SliceZone&gt;</CodeSpan>. It renders each slice as a
          React component, which are written in the <CodeSpan>slices</CodeSpan>{" "}
          directory.
        </Text>
      </>
    ),
  },
  "app/api/exit-preview/route.ts": {
    contents: { __html: app__api__exit_preview__route_ts },
    description: (
      <>
        <Text>
          Content writers can preview Prismic pages before publishing using
          Next.js’ Draft Mode.
        </Text>
        <Text>
          This Route Handler exits the Prismic preview session. The endpoint is
          called when a content writer clicks the “close” button on the Prismic
          toolbar, shown only while previewing a document.
        </Text>
        <Text>
          <CodeSpan>@prismicio/next</CodeSpan> provides a set of helpers that
          make handing previews simple. In this case,{" "}
          <CodeSpan>exitPreview</CodeSpan> takes care of ending the preview
          session.
        </Text>
      </>
    ),
  },
  "app/api/preview/route.ts": {
    contents: { __html: app__api__preview__route_ts },
    description: (
      <>
        <Text>
          Content writers can preview Prismic pages before publishing using
          Next.js’ Draft Mode.
        </Text>
        <Text>
          This Route Handler starts the Prismic preview session. This endpoint
          is called when a content writer clicks the “Preview the page” button
          from within Prismic. It enables Next.js’ Draft Mode and redirects the
          writer to the previewed document’s page.
        </Text>
        <Text>
          <CodeSpan>@prismicio/next</CodeSpan> provides a set of helpers that
          make handing previews simple. In this case,{" "}
          <CodeSpan>redirectToPreviewURL</CodeSpan> takes care of starting the
          preview session.
        </Text>
      </>
    ),
  },
  "app/api/revalidate/route.ts": {
    contents: { __html: app__api__revalidate__route_ts },
    description: (
      <>
        <Text>
          Prismic websites built with Next.js take advantage of Next.js’
          built-in data cache. Using the cache, websites are performant and
          reduce the number of network calls made to Prismic.
        </Text>
        <Text>
          This Route Handler is set up to clear the data cache whenever new
          content is published in Prismic. This is accomplished using a Prismic
          webhook and by tagging all Prismic network requests with{" "}
          <CodeSpan>"prismic"</CodeSpan>.
        </Text>
        <Text>
          The Next.js <CodeSpan>revalidateTag</CodeSpan> function clears all
          Prismic network requests from the data cache.
        </Text>
      </>
    ),
  },
  "prismicio.ts": {
    contents: { __html: prismicio_ts },
    description: (
      <>
        <Text>
          Website content is queried from Prismic using{" "}
          <CodeSpan>@prismicio/client</CodeSpan>. This file configures the
          client.
        </Text>
        <Text>
          Page routes are defined using the <CodeSpan>routes</CodeSpan> option.
          The option contains an array of objects, one for each type of Prismic
          document.
        </Text>
        <Text>
          The <CodeSpan>createClient</CodeSpan> function returns a Prismic
          client, preconfigured to handle Next.js’ <CodeSpan>fetch</CodeSpan>{" "}
          cache. In development, requests are cached for 5 seconds. In
          production, requests are cached until the{" "}
          <CodeSpan>/api/revalidate</CodeSpan> endpoint is called via a Prismic
          webhook.
        </Text>
      </>
    ),
  },
  "package.json": {
    contents: { __html: package_json },
    description: (
      <>
        <Text>Prismic websites use the following dependencies:</Text>
        <UnorderedList>
          <ListItem>
            <CodeSpan>@prismicio/client</CodeSpan>
          </ListItem>
          <ListItem>
            <CodeSpan>@prismicio/react</CodeSpan>
          </ListItem>
          <ListItem>
            <CodeSpan>@prismicio/next</CodeSpan>
          </ListItem>
          <ListItem>Slice Machine</ListItem>
        </UnorderedList>
        <Text>
          These dependencies are installed automatically when creating a website
          with <CodeSpan>@slicemachine/init</CodeSpan>.
        </Text>
      </>
    ),
  },
  "prismicio-types.d.ts": {
    contents: { __html: prismicio_types_d_ts },
    description: (
      <>
        <Text>
          This file is generated by Slice Machine, Prismic’s development tool.
          It provides TypeScript types for all of a website’s content. The types
          are automatically recognized by the Prismic client,{" "}
          <CodeSpan>@prismicio/client</CodeSpan>, and are compatible with
          Prismic’s framework SDKs.
        </Text>
        <Text>
          Each type has a short description so you know exactly where you are in
          your content objects.
        </Text>
      </>
    ),
  },
  "slicemachine.config.json": {
    contents: { __html: slicemachine_config_json },
    description: (
      <>
        <Text>
          Slice Machine is Prismic’s development tool. Pages and slices are set
          up in Slice Machine. The app runs on developers’ computers only during
          development.
        </Text>
        <Text>
          The configuration file sets the project’s Prismic repository,
          framework adapter, and list of slice libraries.
        </Text>
        <Text>
          It also configures the Slice Simulator route, which is used to preview
          slices while developing them.
        </Text>
      </>
    ),
  },
} satisfies Record<
  string,
  {
    contents: string | { __html: string }
    description: ReactNode
  }
>

type DevDialogProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function DevDialog(props: DevDialogProps) {
  const { open = false, onOpenChange: externalOnOpenChange } = props

  const fileExplorer = useFileExplorer()
  const file = fileExplorer.selected
    ? files[fileExplorer.selected.path.join("/") as keyof typeof files]
    : undefined

  function onOpenChange(open: boolean) {
    setTimeout(() => fileExplorer.setSelected(null), 200)
    externalOnOpenChange?.(open)
  }

  useLayoutEffect(() => {
    if (open) {
      installDarkMode()
    } else {
      setTimeout(() => uninstallDarkMode(), 200)
    }
  }, [open])

  function installDarkMode() {
    const portal = document.getElementById("radix-modal-portal")
    if (portal) installColorScheme(portal, "dark")
  }

  function uninstallDarkMode() {
    const portal = document.getElementById("radix-modal-portal")
    if (portal) uninstallColorScheme(portal)
  }

  function viewOnGitHub() {
    window.open("https://github.com/prismicio-community/prismic-demo-aipixi")
  }

  function viewDeveloperDocs() {
    window.open("https://prismic.io/docs")
  }

  return (
    <Dialog
      open={open}
      size={{
        // Full width minus the 24px padding on the left and right. Max width is 1280px on large screens.
        width: "min(calc(100% - 48px), 1330px)",
        // Full height minus the 15px padding on the top and bottom. Max height is 850px on large screens.
        height: "min(calc(100% - 30px), 900px)",
      }}
      onOpenChange={onOpenChange}
    >
      <DialogHeader icon="code" title="Explore the website's code" />
      <DialogContent>
        <div
          style={{
            height: "100%",
            padding: 32,
            display: "grid",
            gap: 32,
            gridTemplateColumns: "3fr 1fr",
          }}
        >
          <FileExplorer
            selected={fileExplorer.selected}
            onSelectedChange={fileExplorer.setSelected}
            className={styles["fileExplorer"]}
          >
            <FileExplorerList>
              <FileExplorerFolder name="app" defaultOpen>
                <FileExplorerFolder name="[uid]" defaultOpen>
                  <FileExplorerFile
                    name="page.tsx"
                    contents={files["app/[uid]/page.tsx"].contents}
                    endAdornment={<PulsingDot />}
                    onSelect={() => trackDevFileViewed("app/[uid]/page.tsx")}
                  />
                </FileExplorerFolder>
                <FileExplorerFolder name="api">
                  <FileExplorerFolder name="exit-preview">
                    <FileExplorerFile
                      name="route.ts"
                      contents={files["app/api/exit-preview/route.ts"].contents}
                    />
                  </FileExplorerFolder>
                  <FileExplorerFolder name="preview">
                    <FileExplorerFile
                      name="route.ts"
                      contents={files["app/api/preview/route.ts"].contents}
                    />
                  </FileExplorerFolder>
                  <FileExplorerFolder name="revalidate">
                    <FileExplorerFile
                      name="route.ts"
                      contents={files["app/api/revalidate/route.ts"].contents}
                    />
                  </FileExplorerFolder>
                </FileExplorerFolder>
                <FileExplorerFolder name="slice-simulator">
                  <FileExplorerFile
                    name="page.tsx"
                    contents={files["app/slice-simulator/page.tsx"].contents}
                  />
                </FileExplorerFolder>
                <FileExplorerFile
                  name="layout.tsx"
                  contents={files["app/layout.tsx"].contents}
                />
                <FileExplorerFile
                  name="page.tsx"
                  contents={files["app/page.tsx"].contents}
                />
              </FileExplorerFolder>
              <FileExplorerFolder name="customtypes">
                <FileExplorerFolder name="page">
                  <FileExplorerFile
                    name="index.json"
                    contents={files["customtypes/page/index.json"].contents}
                  />
                </FileExplorerFolder>
              </FileExplorerFolder>
              <FileExplorerFolder name="slices" defaultOpen>
                <FileExplorerFolder name="Hero" defaultOpen>
                  <FileExplorerFile
                    name="index.tsx"
                    contents={files["slices/Hero/index.tsx"].contents}
                    endAdornment={<PulsingDot />}
                    onSelect={() => trackDevFileViewed("slices/Hero/index.tsx")}
                  />
                  <FileExplorerFile
                    name="model.json"
                    contents={files["slices/Hero/model.json"].contents}
                  />
                  <FileExplorerFile
                    name="mocks.json"
                    contents={files["slices/Hero/mocks.json"].contents}
                  />
                </FileExplorerFolder>
                <FileExplorerFile
                  name="index.ts"
                  contents={files["slices/index.ts"].contents}
                />
                <FileExplorerLabel label="7 more on GitHub…" />
              </FileExplorerFolder>
              <FileExplorerFile
                name="prismicio.ts"
                contents={files["prismicio.ts"].contents}
                endAdornment={<PulsingDot />}
                onSelect={() => trackDevFileViewed("prismicio.ts")}
              />
              <FileExplorerFile
                name="package.json"
                contents={files["package.json"].contents}
              />
              <FileExplorerFile
                name="prismicio-types.d.ts"
                contents={files["prismicio-types.d.ts"].contents}
              />
              <FileExplorerFile
                name="slicemachine.config.json"
                contents={files["slicemachine.config.json"].contents}
              />
            </FileExplorerList>
            <FileExplorerViewer>
              <Box justifyContent="center" flexDirection="column" height="100%">
                <BlankSlate>
                  <BlankSlateIcon name="prismic" size="large" />
                  <BlankSlateDescription>
                    Open a file in the sidebar
                  </BlankSlateDescription>
                </BlankSlate>
              </Box>
            </FileExplorerViewer>
          </FileExplorer>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              overflow: "auto",
            }}
          >
            {fileExplorer.selected ? (
              <>
                <FileHeading path={fileExplorer.selected.path} />
                {file?.description}
              </>
            ) : (
              <>
                <div>
                  <Text color="grey10">Developers</Text>
                  <Text variant="h2">Explore a Next.js project</Text>
                </div>
                <Text>
                  Prismic works with{" "}
                  <FrameworkMention name="next">Next.js</FrameworkMention>,{" "}
                  <FrameworkMention name="nuxt">Nuxt</FrameworkMention>, and{" "}
                  <FrameworkMention name="sveltekit">
                    SvelteKit
                  </FrameworkMention>
                  . Each framework has a purpose-built Prismic integration that
                  feels intuitive.
                </Text>
                <Text>
                  The concepts shown in this Next.js project apply to Nuxt and
                  SvelteKit projects as well.
                </Text>
                <Text>
                  Open files in the sidebar to see their contents and learn
                  their purpose.
                </Text>
                <Text>
                  We recommend checking out files marked with&nbsp;&nbsp;
                  <PulsingDot />
                  &nbsp;&nbsp;first.
                </Text>
              </>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <DialogActionButton
          color="grey"
          endIcon="openInNew"
          onClick={() => viewOnGitHub()}
        >
          View on GitHub
        </DialogActionButton>
        <DialogActionButton
          color="purple"
          endIcon="openInNew"
          onClick={() => viewDeveloperDocs()}
        >
          Open the docs
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

type FileHeadingProps = {
  path: string[]
}

function FileHeading(props: FileHeadingProps) {
  const { path } = props

  const dirname = path.slice(0, path.length - 1).join("/")
  const basename = last(path)

  return (
    <div>
      {dirname ? <Text color="grey10">{dirname}/</Text> : null}
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <Icon name="insertDriveFile" size="large" color="grey11" />
        <Text variant="h3">{basename}</Text>
      </div>
    </div>
  )
}

type CodeSpanProps = {
  children: ReactNode
}

function CodeSpan(props: CodeSpanProps) {
  const { children } = props

  return <code className={styles["codeSpan"]}>{children}</code>
}

function PulsingDot() {
  return <span role="status" className={styles["pulsingDot"]} />
}

const frameworkIcons = {
  next: NextIcon,
  nuxt: NuxtIcon,
  sveltekit: SvelteIcon,
} as const

type FrameworkMentionProps = {
  name: "next" | "nuxt" | "sveltekit"
  children?: ReactNode
}

function FrameworkMention(props: FrameworkMentionProps) {
  const { name, children } = props

  const FrameworkIcon = frameworkIcons[name]

  return (
    <span className={styles["frameworkMention"]}>
      <FrameworkIcon
        width={17}
        height={17}
        className={styles["frameworkMentionIcon"]}
      />{" "}
      <strong>{children}</strong>
    </span>
  )
}

function NextIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...props}>
      <defs>
        <linearGradient id="c" x1="55.6%" x2="83.2%" y1="56.4%" y2="96.1%">
          <stop offset="0%" stopColor="#FFF" />
          <stop offset="100%" stopColor="#FFF" stopOpacity={0} />
        </linearGradient>
        <linearGradient id="d" x1="50%" x2="50%" y1="0%" y2="73.4%">
          <stop offset="0%" stopColor="#FFF" />
          <stop offset="100%" stopColor="#FFF" stopOpacity={0} />
        </linearGradient>
        <circle id="a" cx={10.8} cy={10.8} r={10.8} />
      </defs>
      <g fill="none" fillRule="evenodd">
        <circle cx={12} cy={12} r={12} fill="#FFF" />
        <g transform="translate(1.3 1.3)">
          <mask id="b" fill="#fff">
            <use xlinkHref="#a" />
          </mask>
          <g fillRule="nonzero" mask="url(#b)">
            <circle cx={10.8} cy={10.8} r={10.8} fill="#000" />
            <path
              fill="url(#c)"
              d="M17.9 18.8 8.3 6.4H6.4V15H8V8.3l8.8 11.4 1.2-.9Z"
            />
            <path fill="url(#d)" d="M13.7 6.5h1.4v8.6h-1.4z" />
          </g>
        </g>
      </g>
    </svg>
  )
}

function NuxtIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16" {...props}>
      <path
        fill="#00DC82"
        d="M13.4 16h9l.7-.2.7-.6.2-.8-.2-.8-6-10.3-.6-.6a2 2 0 0 0-.9-.2c-.3 0-.5 0-.8.2-.2.2-.4.3-.5.6l-1.6 2.6-3-5.1c-.1-.2-.3-.5-.6-.6C9.6 0 9.3 0 9 0c-.2 0-.5 0-.8.2l-.6.6L.2 13.6c-.2.2-.2.5-.2.8 0 .3 0 .6.2.8l.6.6.8.2h5.6c2.2 0 3.8-1 5-2.9l2.7-4.7 1.4-2.5 4.4 7.5H15L13.4 16Zm-6.3-2.6H3.2L9 3.4l3 5-2 3.4c-.7 1.2-1.6 1.6-2.9 1.6Z"
      />
    </svg>
  )
}

function SvelteIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 24" {...props}>
      <g fill="none">
        <path
          fill="#FF3E00"
          d="M18.7 3.2A7.3 7.3 0 0 0 8.9 1L3.3 4.6A6.4 6.4 0 0 0 .4 9c-.3 1.5 0 3 .7 4.4-.5.7-.8 1.5-1 2.4-.3 1.8.1 3.6 1.2 5.1A7.3 7.3 0 0 0 11 23l5.6-3.5c1.5-1 2.6-2.5 2.9-4.3.2-1.5 0-3-.7-4.4.5-.7.8-1.5 1-2.4.3-1.8-.1-3.6-1.2-5.1"
        />
        <path
          fill="#FFF"
          d="M8.3 21.1c-1.8.5-3.7-.2-4.7-1.7a4.1 4.1 0 0 1-.6-3.7l.1-.3.3.2c.7.5 1.4.9 2.2 1.1h.2v.3c0 .3 0 .6.2.8.3.5.9.7 1.4.5l.4-.1 5.6-3.6c.2-.1.4-.4.5-.7 0-.4 0-.7-.2-1-.3-.4-1-.7-1.5-.5l-.3.1L9.8 14a4 4 0 0 1-1.2.5c-1.8.5-3.7-.2-4.7-1.8-.7-.9-1-2-.7-3 .2-1.1.8-2 1.7-2.7l5.6-3.5a4 4 0 0 1 1.1-.5c1.8-.5 3.7.2 4.8 1.7a4.1 4.1 0 0 1 .6 3.7l-.1.3-.3-.2c-.7-.5-1.4-.9-2.2-1.1h-.2V7c0-.3 0-.6-.2-.8-.3-.5-1-.7-1.5-.5l-.3.1-5.6 3.6c-.3.1-.5.4-.5.7 0 .4 0 .7.2 1 .3.4.9.7 1.4.5l.4-.1 2.1-1.4a4 4 0 0 1 1.1-.5c1.8-.5 3.7.2 4.8 1.8a3.9 3.9 0 0 1-1 5.7l-5.6 3.5a4 4 0 0 1-1.2.5"
        />
      </g>
    </svg>
  )
}
